/**
 * SCRUM-249 (WH-7) — Zod schemas for the org-level pricing provider-connection
 * HTTP boundary (DS-017 §12.1).
 *
 * Endpoints covered:
 *   POST   /organizations/:slug/pricing/provider-connections        ConnectProviderToOrganization
 *   DELETE /organizations/:slug/pricing/provider-connections/:id     DisconnectProviderFromOrganization (If-Match header, no body)
 *   GET    /organizations/:slug/pricing/provider-connections         ListProviderConnections
 *
 * `ExternalPricingProvider` domain values: 'wheelhouse' | 'pricelabs'
 * (from domain/grid/smart-cell.ts). `ProviderConnectionStatus`:
 * 'inactive' | 'connecting' | 'active' | 'disconnecting'
 * (from domain/provider-connection/provider-connection.aggregate.ts).
 *
 * SECURITY INVARIANT: the list-item response NEVER carries credential material
 * (credentialsRef / webhookSecretRef / rawCredentials). The item schema is an
 * explicit allow-list of safe fields — see ProviderConnectionItemSchema.
 *
 * Adversarial tests: packages/validators/src/__tests__/pricing/provider-connection.spec.ts
 */
import { z } from 'zod';
import { instantString } from './_shared.js';
// ---------------------------------------------------------------------------
// Shared enums (mirror the domain string-union types)
// ---------------------------------------------------------------------------
export const ProviderKindSchema = z.enum(['wheelhouse', 'pricelabs']);
export const ProviderConnectionStatusSchema = z.enum([
    'inactive',
    'connecting',
    'active',
    'disconnecting',
]);
// ---------------------------------------------------------------------------
// ConnectProviderToOrganizationRequestSchema — POST body
// ---------------------------------------------------------------------------
/**
 * `rawCredentials`: plaintext credential ref the org admin pastes; the use case
 * encrypts it through the KMS seam before persistence. Bounded ≤1024 to cap
 * request size; never logged, never echoed back.
 * `externalUserId`: optional WH-side user id for the webhook chain-of-trust
 * (§9.1). Absent → null.
 */
export const ConnectProviderToOrganizationRequestSchema = z
    .object({
    providerKind: ProviderKindSchema,
    rawCredentials: z.string().min(1).max(1024),
    externalUserId: z.string().min(1).max(255).nullable().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------
export const ConnectProviderToOrganizationResponseSchema = z.object({
    providerConnectionId: z.string().uuid(),
});
/**
 * ProviderConnectionItemSchema — one entry in the list response.
 *
 * EXPLICIT safe-field allow-list. credentialsRef / webhookSecretRef are
 * deliberately absent; a security test asserts the serializer emits EXACTLY
 * these keys so any future aggregate field fails closed (does not leak).
 */
export const ProviderConnectionItemSchema = z.object({
    id: z.string().uuid(),
    providerKind: ProviderKindSchema,
    status: ProviderConnectionStatusSchema,
    externalUserId: z.string().nullable(),
    version: z.number().int().nonnegative(),
    createdAt: instantString,
    updatedAt: instantString,
});
export const ProviderConnectionListResponseSchema = z.object({
    items: z.array(ProviderConnectionItemSchema),
});
//# sourceMappingURL=provider-connection.js.map